import { PlaylistAdd as PlaylistAddIcon } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNotification } from "../../contexts/NotificationContext";
import {
  ResearchProject,
  ResearchTrackBasic,
  researchApi,
} from "../../services/researchApi";

interface AddToTrackButtonProps {
  entityType: "organization" | "facility" | "project";
  entityId: string;
  entityName: string;
  variant?: "button" | "icon";
  size?: "small" | "medium" | "large";
  onSuccess?: () => void;
}

const AddToTrackButton: React.FC<AddToTrackButtonProps> = ({
  entityType,
  entityId,
  entityName,
  variant = "button",
  size = "medium",
  onSuccess,
}) => {
  const { showNotification } = useNotification();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [projects, setProjects] = useState<ResearchProject[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>("");
  const [selectedTrack, setSelectedTrack] = useState<string>("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (dialogOpen) {
      fetchProjects();
    }
  }, [dialogOpen]);

  useEffect(() => {
    setSelectedTrack("");
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const response = await researchApi.getProjects();
      const editableProjects = response.results.filter(
        (p) =>
          p.permission_level === "OWNER" || p.permission_level === "EDITOR",
      );
      setProjects(editableProjects);
    } catch (error) {
      showNotification("Failed to load projects", "error");
    }
  };

  const selectedProjectObj = projects.find((p) => p.id === selectedProject);
  const tracks: ResearchTrackBasic[] = selectedProjectObj
    ? selectedProjectObj.tracks.filter((t) => t.status === "active")
    : [];

  const handleOpen = () => {
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setSelectedProject("");
    setSelectedTrack("");
    setReason("");
  };

  const handleAddToTrack = async () => {
    if (!selectedTrack) return;

    try {
      setLoading(true);

      const result = await researchApi.addEntityToTrack({
        track: selectedTrack,
        kg_entity_id: entityId,
        kg_entity_type: entityType,
        reason: reason.trim() || undefined,
      });

      const trackName =
        tracks.find((t) => t.id === selectedTrack)?.name || "track";

      if (result.status === "created") {
        showNotification(`Added "${entityName}" to ${trackName}`, "success");
      } else {
        showNotification(`"${entityName}" is already in ${trackName}`, "info");
      }

      handleClose();
      onSuccess?.();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.detail ||
        "Failed to add entity to track. Please try again.";
      showNotification(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const selectedTrackName =
    tracks.find((t) => t.id === selectedTrack)?.name || "";

  return (
    <>
      {variant === "icon" ? (
        <Tooltip title="Add to Research Track">
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleOpen();
            }}
            size={size}
            sx={{
              color: "primary.main",
              "&:hover": {
                backgroundColor: "primary.main",
                color: "white",
              },
            }}
          >
            <PlaylistAddIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          startIcon={<PlaylistAddIcon />}
          onClick={(e) => {
            e.stopPropagation();
            handleOpen();
          }}
          variant="outlined"
          size={size}
        >
          Add to Track
        </Button>
      )}

      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: { backgroundColor: "#1e1e1e" },
        }}
      >
        <DialogTitle sx={{ backgroundColor: "#1e1e1e" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PlaylistAddIcon />
            Add to Research Track
          </Box>
        </DialogTitle>

        <DialogContent sx={{ backgroundColor: "#1e1e1e" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            {/* Entity info */}
            <Typography variant="body2" color="text.secondary">
              Adding <strong>{entityName}</strong> ({entityType})
            </Typography>

            {/* Project selection */}
            <Autocomplete
              options={projects}
              getOptionLabel={(option) => option.name}
              value={selectedProjectObj || null}
              onChange={(_event, newValue) => {
                setSelectedProject(newValue?.id || "");
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Project"
                  placeholder="Search projects..."
                  size="small"
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Typography variant="body2">{option.name}</Typography>
                </li>
              )}
              slotProps={{
                paper: {
                  sx: {
                    backgroundColor: "background.raised",
                    "& .MuiAutocomplete-option": {
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.08)",
                      },
                      '&[aria-selected="true"]': {
                        backgroundColor: "rgba(25, 118, 210, 0.2)",
                      },
                    },
                  },
                },
              }}
            />

            {/* Track selection */}
            <Autocomplete
              options={tracks}
              getOptionLabel={(option) => option.name}
              value={tracks.find((t) => t.id === selectedTrack) || null}
              onChange={(_event, newValue) => {
                setSelectedTrack(newValue?.id || "");
              }}
              disabled={!selectedProject}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText={
                selectedProject
                  ? "No active tracks in this project"
                  : "Select a project first"
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Track"
                  placeholder="Search tracks..."
                  size="small"
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Typography variant="body2">{option.name}</Typography>
                </li>
              )}
              slotProps={{
                paper: {
                  sx: {
                    backgroundColor: "background.raised",
                    "& .MuiAutocomplete-option": {
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.08)",
                      },
                      '&[aria-selected="true"]': {
                        backgroundColor: "rgba(25, 118, 210, 0.2)",
                      },
                    },
                  },
                },
              }}
            />

            {/* Reason */}
            <TextField
              label="Reason (optional)"
              multiline
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why are you adding this entity?"
              fullWidth
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ backgroundColor: "#1e1e1e" }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleAddToTrack}
            variant="contained"
            disabled={!selectedTrack || loading}
          >
            {loading ? "Adding..." : `Add to ${selectedTrackName || "Track"}`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddToTrackButton;
